import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";
import { useCatalog } from "@/context/CatalogContext";
import { LoadingState, PageHeader, StatTile, StatePill, apiErrorMessage } from "@/components/admin/AdminUI";

export default function AdminOverview() {
  const { byKey } = useCatalog();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin-center/overview")
      .then(({ data: payload }) => setData(payload))
      .catch((err) => setError(apiErrorMessage(err)));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return <LoadingState />;

  const platform = data.scope === "platform";
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHeader
        icon={LayoutDashboard}
        title={platform ? "Visão geral da plataforma" : "Visão geral da empresa"}
        description={
          platform
            ? "Empresas clientes, usuários, catálogo e governança em um só lugar."
            : "Usuários, acessos e atividades da sua empresa."
        }
      />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={platform ? "Empresas" : "Minhas empresas"} value={data.tenants.total}
          hint={data.tenants.suspended ? `${data.tenants.suspended} suspensa(s)` : "todas ativas"} testId="overview-tenants" />
        <StatTile label="Usuários" value={data.users.total} hint={`${data.users.active} ativo(s)`} testId="overview-users" />
        <StatTile label="Módulos do catálogo" value={data.catalog.total}
          hint={data.catalog.disabled.length ? `${data.catalog.disabled.length} desabilitado(s)` : "todos habilitados"} />
        <StatTile label="Lixeira de ocorrências" value={data.trash_count} hint="restauráveis" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Usuários por perfil</h2>
          <ul className="space-y-1.5">
            {Object.entries(data.users.by_role)
              .sort((a, b) => b[1] - a[1])
              .map(([role, count]) => (
                <li key={role} className="flex items-center justify-between text-sm">
                  <span>{ROLE_LABELS[role] || role}</span>
                  <span className="tabular-nums text-muted-foreground">{count}</span>
                </li>
              ))}
          </ul>
          {platform && data.access_requests_pending > 0 && (
            <p className="mt-3 text-xs">
              <StatePill tone="warning">{data.access_requests_pending} solicitação(ões) de acesso pendente(s)</StatePill>{" "}
              {byKey.administracao_plataforma?.available && (
                <Link className="text-primary underline" to="/administracao-plataforma">revisar</Link>
              )}
            </p>
          )}
          {data.catalog.disabled.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Desabilitados na plataforma:{" "}
              {data.catalog.disabled.map((key) => byKey[key]?.label || key).join(", ")}
            </p>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Atividade administrativa recente</h2>
          {data.recent_audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum registro.</p>
          ) : (
            <ul className="space-y-2">
              {data.recent_audit.map((item, index) => (
                <li key={`${item.created_at}-${index}`} className="text-xs">
                  <span className="text-muted-foreground">{formatDate(item.created_at)}</span>{" "}
                  <strong>{item.user_name || "—"}</strong> · {item.detail || item.action}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
